import { yupResolver } from '@hookform/resolvers/yup';
import { ArrowUpRight, Clock, Command, CornerDownLeft } from '@untitled-ui/icons-react/build/cjs';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import AutoCompleteGoogle from 'src/components/AutoCompleteGoogle/AutoCompleteGoogle';
import ButtonCmp from 'src/components/ButtonCmp';
import InputMaskCmp from 'src/components/InputMaskCmp';
import InputText from 'src/components/InputText/InputText';
import MapBox from 'src/components/MapBox/MapBox';
import { setConsigneeOptions, setConsignees, setShipperOptions, setShippers } from 'src/redux/address.slice';
import { useAppDispatch } from 'src/redux/hooks';
import { createAddress, updateSingleAddress } from 'src/services/AddressBookServices';
import { getDuplicateAddressMessage } from 'src/utils/addressBookDuplicate';
import { formatTimeInput, getSelectBoxOptions, getMissingAddressMessage, isShipperAddressIncomplete, normalizeTimeValue } from 'src/utils/CommonFunctions';
import * as Yup from 'yup';

import Address from '../../../AddressDetails/Address';

interface IAddress {
    address1: string;
    city: string;
    country: string;
    countryCode: string;
    fullAddress?: string;
    latitude?: string;
    longitude?: string;
    postal?: string;
    state?: string;
    stateCode?: string;
    streetNumber?: string;
    timezone?: string;
    additionalServiceIds?: number[];
}

interface IFormData {
    companyName?: string;
    address?: IAddress | null;
    businessHoursStartTime?: string;
    businessHoursEndTime?: string;
}

const toHHMM = (val?: string | null): string => (val ? String(val).slice(0, 5) : '');

interface ShipperFormProps {
    handleEditShipper: (isEdit?: boolean) => () => void;
    id?: number | null;
    handleSaveShipper: (data: IFormData) => void;
    isShipper?: boolean;
    isOpenFromQuoting?: boolean;
    isOpenFromStop?: boolean;
    defaultCompanyName?: string;
    defaultAddress?: IAddress | null;
    showAddressIncompleteWarning?: boolean;
}

const ShipperConsigneeForm = ({
    handleEditShipper,
    id,
    handleSaveShipper,
    isShipper = true,
    isOpenFromQuoting = false,
    isOpenFromStop = false,
    defaultCompanyName = '',
    defaultAddress = null,
    showAddressIncompleteWarning = false,
}: ShipperFormProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { googleMapStatus } = useSelector((state: any) => state.map);
    const { shippers, consignees, shipperOptions, consigneeOptions } = useSelector((state: any) => state.addresses);

    const [isAddressOpen, setIsAddressOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const timeFieldSchema = Yup.string()
        .nullable()
        .transform((val) => (val ? val : ''))
        .test('valid-time', t('ValidationMessages.Invalid_time_format_HH_MM'), (value) => {
            if (!value) return true;

            return /^([01]\d|2[0-3]):([0-5]\d)$/.test(value);
        });

    const validationSchema = Yup.object().shape({
        companyName: Yup.string().required('Shipper name is required'),
        businessHoursStartTime: timeFieldSchema.test('start-required-with-end', t('ValidationMessages.Business_Hours_Both_Required'), function (value) {
            const { businessHoursEndTime } = this.parent;

            if (businessHoursEndTime && !value) return false;

            return true;
        }),
        businessHoursEndTime: timeFieldSchema
            .test('end-required-with-start', t('ValidationMessages.Business_Hours_Both_Required'), function (value) {
                const { businessHoursStartTime } = this.parent;

                if (businessHoursStartTime && !value) return false;

                return true;
            })
            .test('end-after-start', t('ValidationMessages.Business_Hours_End_After_Start'), function (value) {
                const { businessHoursStartTime } = this.parent;
                if (!businessHoursStartTime || !value) return true;

                return value > businessHoursStartTime;
            }),
        address: isOpenFromQuoting
            ? Yup.object()
                  .shape({
                      fullAddress: Yup.string().required('Shipper address is required'),
                  })
                  .required('Shipper address is required')
            : Yup.object()
                  .shape({
                      fullAddress: Yup.string().required('Shipper address is required'),
                      address1: Yup.string().required('Address1 is required'),
                      city: Yup.string().required('City is required'),
                      country: Yup.string().required('Country is required'),
                      countryCode: Yup.string().required('Country code is required'),
                      latitude: Yup.string().required('Latitude is required'),
                      longitude: Yup.string().required('Longitude is required'),
                      postal: Yup.string().required('Postal code is required'),
                      state: Yup.string().required('State is required'),
                      stateCode: Yup.string().required('State code is required'),
                      streetNumber: Yup.string().required('Street number is required'),
                  })
                  .required('Shipper address is required'),
    }) as Yup.ObjectSchema<IFormData>;

    const {
        watch,
        control,
        setValue,
        getValues,
        handleSubmit,
        clearErrors,
        setError,
        formState: { errors },
    } = useForm<IFormData>({
        resolver: yupResolver(validationSchema),
        mode: 'onSubmit',
        defaultValues: {
            // When id is provided, prefer the loaded address-book entry; fall back to
            // defaultAddress (passed from the parent) when the entry isn't in the
            // loaded array yet (e.g. the consignee is on a different pagination page).
            address: id
                ? (isShipper ? shippers.find((shipper: any) => shipper.id === id) : consignees.find((consignee: any) => consignee.id === id)) || defaultAddress || null
                : defaultAddress || null,
            companyName: id
                ? (isShipper ? shippers.find((shipper: any) => shipper.id === id)?.companyName : consignees.find((consignee: any) => consignee.id === id)?.companyName) || defaultCompanyName || ''
                : defaultCompanyName || '',
            businessHoursStartTime: id
                ? toHHMM((isShipper ? shippers.find((s: any) => s.id === id) : consignees.find((c: any) => c.id === id))?.businessHoursStartTime ?? (defaultAddress as any)?.businessHoursStartTime)
                : '09:00',
            businessHoursEndTime: id
                ? toHHMM((isShipper ? shippers.find((s: any) => s.id === id) : consignees.find((c: any) => c.id === id))?.businessHoursEndTime ?? (defaultAddress as any)?.businessHoursEndTime)
                : '15:00',
        },
    });
    const watchShipperAddress = watch('address');
    const watchShipperFullAddress = watch('address')?.fullAddress;
    const watchCompanyName = watch('companyName');
    const watchBusinessHoursStartTime = watch('businessHoursStartTime');
    const watchBusinessHoursEndTime = watch('businessHoursEndTime');

    const handleStartTimeChange = (e: any) => {
        setValue('businessHoursStartTime', formatTimeInput(e.target.value, watchBusinessHoursStartTime || ''));
        clearErrors('businessHoursStartTime');
    };

    const handleEndTimeChange = (e: any) => {
        setValue('businessHoursEndTime', formatTimeInput(e.target.value, watchBusinessHoursEndTime || ''));
        clearErrors('businessHoursEndTime');
    };

    const handleStartTimeBlur = () => {
        const normalized = normalizeTimeValue(watchBusinessHoursStartTime);
        if (normalized && normalized !== watchBusinessHoursStartTime) setValue('businessHoursStartTime', normalized);
    };

    const handleEndTimeBlur = () => {
        const normalized = normalizeTimeValue(watchBusinessHoursEndTime);
        if (normalized && normalized !== watchBusinessHoursEndTime) setValue('businessHoursEndTime', normalized);
    };

    // Store initial form values - compute from same logic as defaultValues
    const initialValuesRef = useRef<IFormData | null>(null);
    useEffect(() => {
        if (initialValuesRef.current === null) {
            const initialAddress = id
                ? (isShipper ? shippers.find((shipper: any) => shipper.id === id) : consignees.find((consignee: any) => consignee.id === id)) || defaultAddress || null
                : defaultAddress || null;
            const initialCompanyName = id
                ? (isShipper ? shippers.find((shipper: any) => shipper.id === id)?.companyName : consignees.find((consignee: any) => consignee.id === id)?.companyName) || defaultCompanyName || ''
                : defaultCompanyName || '';

            const record = id ? (isShipper ? shippers.find((s: any) => s.id === id) : consignees.find((c: any) => c.id === id)) : null;

            initialValuesRef.current = {
                companyName: initialCompanyName,
                address: initialAddress ? JSON.parse(JSON.stringify(initialAddress)) : null,
                businessHoursStartTime: record ? toHHMM(record?.businessHoursStartTime ?? (defaultAddress as any)?.businessHoursStartTime) : '09:00',
                businessHoursEndTime: record ? toHHMM(record?.businessHoursEndTime ?? (defaultAddress as any)?.businessHoursEndTime) : '15:00',
            };
        }
    }, [id, isShipper, shippers, consignees, defaultCompanyName, defaultAddress]);

    // Compare current values with initial values
    const hasFormChanged = useMemo(() => {
        if (initialValuesRef.current === null) {
            return false;
        }

        // Compare company name
        if (watchCompanyName !== initialValuesRef.current.companyName) {
            return true;
        }

        // Compare address
        const currentAddress = watchShipperAddress;
        const initialAddress = initialValuesRef.current.address;

        // If both are null, no change
        if (!currentAddress && !initialAddress) {
            return false;
        }

        // If one is null and the other is not, there's a change
        if (!currentAddress || !initialAddress) {
            return true;
        }

        // Deep compare address objects
        const addressFields: (keyof IAddress)[] = ['fullAddress', 'address1', 'city', 'country', 'countryCode', 'latitude', 'longitude', 'postal', 'state', 'stateCode', 'streetNumber', 'timezone'];

        for (const field of addressFields) {
            if (currentAddress[field] !== initialAddress[field]) {
                return true;
            }
        }

        // Compare additionalServiceIds arrays
        const currentServiceIds = currentAddress.additionalServiceIds?.sort().join(',') || '';
        const initialServiceIds = initialAddress.additionalServiceIds?.sort().join(',') || '';

        if (currentServiceIds !== initialServiceIds) {
            return true;
        }

        // Compare business hours
        if ((watchBusinessHoursStartTime || '') !== (initialValuesRef.current.businessHoursStartTime || '')) {
            return true;
        }

        if ((watchBusinessHoursEndTime || '') !== (initialValuesRef.current.businessHoursEndTime || '')) {
            return true;
        }

        return false;
    }, [watchCompanyName, watchShipperAddress, watchBusinessHoursStartTime, watchBusinessHoursEndTime]);

    const handleAddressInputChange = (value: string) => {
        // Handle string input from typing in the address field
        if (value === '') {
            setValue('address', null);
            clearErrors('address');

            return;
        }

        // Initialize address object if it doesn't exist
        setValue('address', {
            fullAddress: value,
            address1: '',
            city: '',
            country: '',
            countryCode: '',
            latitude: '',
            longitude: '',
            postal: '',
            state: '',
            stateCode: '',
            streetNumber: '',
            timezone: '',
            additionalServiceIds: [],
        });

        clearErrors('address');
    };

    /**
     * Name auto-fill from a Google establishment (parity with WALHQ, which fills the party name
     * from the picked place). `AutoCompleteGoogle` already returns `companyName` — it is
     * `place.name` when the place's types include 'establishment', and '' for a plain street
     * address, so a non-business pick never wipes anything.
     *
     * Only overwrites a name the user did not author: an empty field, or one this same auto-fill
     * put there. That way typing "Warehouse B" then picking its address keeps "Warehouse B", while
     * correcting a wrong business pick still swaps the name over to the new business.
     */
    const googleFilledCompanyNameRef = useRef<string | null>(null);

    const applyGoogleCompanyName = (googleCompanyName?: string) => {
        if (!googleCompanyName) return;

        const currentCompanyName = getValues('companyName')?.trim() ?? '';

        if (currentCompanyName && currentCompanyName !== googleFilledCompanyNameRef.current) return;

        googleFilledCompanyNameRef.current = googleCompanyName;
        setValue('companyName', googleCompanyName);
        clearErrors('companyName');
    };

    const handleAutocompleteChange = (value: any) => {
        if (!value) {
            if (!watchShipperFullAddress) {
                setValue('address', null);
            }

            return;
        }

        if (value.type === 'blur' || (value.target && value.type)) {
            if (!watchShipperFullAddress) {
                setValue('address', null);
            }

            return;
        }

        if (typeof value === 'object' && !value.target && (value.fullAddress === '' || (!value.fullAddress && !value.address1 && !value.city))) {
            setValue('address', null);

            return;
        }

        if (typeof value === 'object' && !value.target && value.fullAddress !== undefined) {
            setValue('address', {
                ...(watchShipperAddress || {}),
                fullAddress: value.fullAddress ?? '',
                address1: value.address1 ?? watchShipperAddress?.address1 ?? '',
                city: value.city ?? watchShipperAddress?.city ?? '',
                country: value.country ?? watchShipperAddress?.country ?? '',
                countryCode: value.countryCode ?? watchShipperAddress?.countryCode ?? '',
                latitude: value.latitude ?? watchShipperAddress?.latitude ?? '',
                longitude: value.longitude ?? watchShipperAddress?.longitude ?? '',
                postal: value.postal ?? watchShipperAddress?.postal ?? '',
                state: value.state ?? watchShipperAddress?.state ?? '',
                stateCode: value.stateCode ?? watchShipperAddress?.stateCode ?? '',
                streetNumber: value.streetNumber ?? watchShipperAddress?.streetNumber ?? '',
                timezone: value.timezone ?? watchShipperAddress?.timezone ?? '',
                additionalServiceIds: value.additionalServiceIds ?? watchShipperAddress?.additionalServiceIds ?? [],
            });
            clearErrors('address');
            applyGoogleCompanyName(value.companyName);
        }
    };
    const MapComponent = useMemo(
        () => (
            <MapBox
                draggable={false}
                address={watchShipperAddress && watchShipperAddress.fullAddress && watchShipperAddress.fullAddress !== '' ? watchShipperAddress : null}
                id="shipperMap"
                isDefaultSatelliteView={true}
                onReverseAddressFunc={handleAutocompleteChange}
                className="rounded-lg bg-white h-full border border-utilityGray200"
            />
        ),
        [watchShipperAddress, watchShipperFullAddress]
    );

    const handleSave = useCallback(
        async (data: IFormData) => {
            if (id) {
                setIsLoading(true);
                const payload = {
                    ...data.address,
                    additionalServiceIds: data.address?.additionalServiceIds?.map((serviceId: number) => +serviceId),
                    addressType: isShipper ? 'SHIPPER' : 'CONSIGNEE',
                    companyName: data.companyName,
                    limitedAccessType: null,
                    businessHoursStartTime: data.businessHoursStartTime || null,
                    businessHoursEndTime: data.businessHoursEndTime || null,
                };

                updateSingleAddress({ id, ...payload })
                    .then((result: any) => {
                        if (result) {
                            const updatedItem = {
                                id: result.data.id,
                                companyName: result.data.companyName,
                                ...result.data,
                            };

                            // Update the arrays with the modified shipper/consignee
                            if (isShipper) {
                                const updatedShippers = shippers.map((item: any) => (item.id === updatedItem.id ? updatedItem : item));
                                dispatch(setShippers(updatedShippers));

                                const updatedOptions = getSelectBoxOptions(updatedShippers.slice(0, 5), 'id', 'companyName');
                                dispatch(setShipperOptions(updatedOptions));
                            } else {
                                const updatedConsignees = consignees.map((item: any) => (item.id === updatedItem.id ? updatedItem : item));
                                dispatch(setConsignees(updatedConsignees));

                                const updatedOptions = getSelectBoxOptions(updatedConsignees.slice(0, 5), 'id', 'companyName');
                                dispatch(setConsigneeOptions(updatedOptions));
                            }

                            handleSaveShipper(updatedItem);
                        }
                    })
                    .catch((error: any) => {
                        const duplicateMessage = getDuplicateAddressMessage(error, t('AddressBook.Duplicate_Address_Exists'));

                        if (duplicateMessage) {
                            // A duplicate is not a malformed address, so the generic
                            // "address is required" hint would send the user off re-picking a place
                            // that was already correct. Flag the name instead and say why, on the
                            // field's alert icon rather than in a toast (client, 2026-10-09).
                            setError('companyName', { type: 'server', message: duplicateMessage });

                            return;
                        }

                        if (isOpenFromQuoting && error?.response?.data) {
                            setError('address', {
                                type: 'manualWithTooltip',
                                message: 'Shipper address is required',
                            });
                        }
                    })
                    .finally(() => {
                        setIsLoading(false);
                    });
            } else {
                setIsLoading(true);
                const payload = {
                    ...data.address,
                    additionalServiceIds: data.address?.additionalServiceIds?.map((serviceId: number) => +serviceId),
                    id: null,
                    addressType: isShipper ? 'SHIPPER' : 'CONSIGNEE',
                    companyName: data.companyName,
                    type: isShipper ? 1 : 2,
                    businessHoursStartTime: data.businessHoursStartTime || null,
                    businessHoursEndTime: data.businessHoursEndTime || null,
                };
                createAddress(payload)
                    .then((result: any) => {
                        if (result) {
                            const newItem = {
                                id: result.data.id,
                                companyName: result.data.companyName,
                                type: isShipper ? 1 : 2,
                                ...result.data,
                            };

                            // Prepend the new shipper/consignee so it always appears in the first 5 options
                            if (isShipper) {
                                const updatedShippers = [newItem, ...shippers];
                                dispatch(setShippers(updatedShippers));

                                const updatedOptions = getSelectBoxOptions(updatedShippers.slice(0, 5), 'id', 'companyName');
                                dispatch(setShipperOptions(updatedOptions));
                            } else {
                                const updatedConsignees = [newItem, ...consignees];
                                dispatch(setConsignees(updatedConsignees));

                                const updatedOptions = getSelectBoxOptions(updatedConsignees.slice(0, 5), 'id', 'companyName');
                                dispatch(setConsigneeOptions(updatedOptions));
                            }

                            handleSaveShipper(newItem);
                        }
                    })
                    .catch((error: any) => {
                        const duplicateMessage = getDuplicateAddressMessage(error, t('AddressBook.Duplicate_Address_Exists'));

                        if (duplicateMessage) {
                            // A duplicate is not a malformed address, so the generic
                            // "address is required" hint would send the user off re-picking a place
                            // that was already correct. Flag the name instead and say why, on the
                            // field's alert icon rather than in a toast (client, 2026-10-09).
                            setError('companyName', { type: 'server', message: duplicateMessage });

                            return;
                        }

                        if (isOpenFromQuoting && error?.response?.data) {
                            setError('address', {
                                type: 'manualWithTooltip',
                                message: 'Shipper address is required',
                            });
                        }
                    })
                    .finally(() => {
                        setIsLoading(false);
                    });
            }
        },
        [id, isShipper, handleSaveShipper, dispatch, shippers, consignees, shipperOptions, consigneeOptions]
    );

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            // Check for Cmd+Enter (Mac) or Ctrl+Enter (Windows/Linux)
            if ((event.metaKey || event.ctrlKey) && (event.key === 'Enter' || event.key === 'Return')) {
                if (!hasFormChanged || isLoading) {
                    event.preventDefault();

                    return;
                }
                event.preventDefault();
                handleSubmit(handleSave)();
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleSubmit, handleSave, hasFormChanged, isLoading]);

    return (
        <>
            <div className="stepper-content-container">
                <div className="stepper-lable-wrap flex">
                    <label className="stepper-label-common">
                        {isOpenFromStop ? t('Common.Name') : isShipper ? 'Shipper Name' : 'Consignee Name'}
                        <span className="text-red-600 ml-[2px] leading-4 text-xs">*</span>
                    </label>
                </div>
                <Controller
                    name="companyName"
                    control={control}
                    render={({ field: { value }, fieldState: { error } }) => (
                        <InputText
                            inputName="companyName"
                            placeholder={
                                isOpenFromStop ? t('Orders.Enter_name') : isShipper ? t('QuotingHub.QuotingRequest.Enter_shipper_name') : t('QuotingHub.QuotingDashboard.Enter_consignee_name')
                            }
                            parentClassName="w-full"
                            value={value}
                            onChangeFunc={(e) => {
                                setValue('companyName', e.target.value);
                                clearErrors('companyName');
                            }}
                            isError={!!error}
                            // Server-side rejections (the duplicate name+address guard) ride on the alert icon
                            // inside the field, on hover. Required-field errors stay border-only per the house
                            // rule, so the message is gated on the `server` error type rather than on any error.
                            errorText={error?.type === 'server' ? error.message : undefined}
                            isIconError={error?.type === 'server'}
                            maxLength={50}
                        />
                    )}
                />
            </div>
            <div className="stepper-content-container field-without-top-border pt-5">
                <div className="stepper-lable-wrap flex">
                    <label className="stepper-label-common">{t('Common.Business_Hours')}</label>
                </div>
                <div className="flex gap-x-3 flex-1">
                    <Controller
                        name="businessHoursStartTime"
                        control={control}
                        render={({ field: { value }, fieldState: { error } }) => (
                            <InputMaskCmp
                                inputName="businessHoursStartTime"
                                parentClassName="flex-1"
                                placeholder={t('Common.Start_Time')}
                                value={value || ''}
                                HandleChange={handleStartTimeChange}
                                handleBlur={handleStartTimeBlur}
                                mask="99:99"
                                icon={
                                    <div className="absolute top-1/2 -translate-y-1/2 right-3">
                                        <Clock className="h-4 w-4" />
                                    </div>
                                }
                                isError={!!error?.message}
                            />
                        )}
                    />
                    <Controller
                        name="businessHoursEndTime"
                        control={control}
                        render={({ field: { value }, fieldState: { error } }) => (
                            <InputMaskCmp
                                inputName="businessHoursEndTime"
                                parentClassName="flex-1"
                                placeholder={t('Common.End_Time')}
                                value={value || ''}
                                HandleChange={handleEndTimeChange}
                                handleBlur={handleEndTimeBlur}
                                mask="99:99"
                                icon={
                                    <div className="absolute top-1/2 -translate-y-1/2 right-3">
                                        <Clock className="h-4 w-4" />
                                    </div>
                                }
                                isError={!!error?.message}
                            />
                        )}
                    />
                </div>
            </div>
            <div className="stepper-content-container field-without-top-border pt-5">
                <div className="stepper-lable-wrap flex">
                    <label className="stepper-label-common">
                        {isOpenFromStop ? t('Common.Address') : isShipper ? 'Shipper Address' : 'Consignee Address'}
                        <span className="text-red-600 ml-[2px] leading-4 text-xs">*</span>
                    </label>
                </div>
                <div className="flex-1 space-y-3">
                    <div className="flex-1 flex gap-x-2">
                        <Controller
                            name={`address.fullAddress`}
                            control={control}
                            render={({ fieldState: { error } }) => (
                                <AutoCompleteGoogle
                                    inputName={`fullAddress`}
                                    placeholder={t('QuotingHub.QuotingDashboard.Enter_address')}
                                    className={`form_control truncate`}
                                    labelClassName="mb-1.5 block w-full"
                                    mapParentClass="w-full"
                                    isClearable
                                    onAddressInputChange={handleAddressInputChange}
                                    onChangeFunc={handleAutocompleteChange}
                                    onBlur={handleAutocompleteChange}
                                    value={watchShipperFullAddress || ''}
                                    errorText={!!errors?.address}
                                    errorTextTooltip={
                                        error?.type === 'manualWithTooltip'
                                            ? error?.message
                                            : showAddressIncompleteWarning && isShipperAddressIncomplete(watchShipperAddress)
                                            ? getMissingAddressMessage(watchShipperAddress, t) || null
                                            : null
                                    }
                                    disabled={false}
                                />
                            )}
                        />
                    </div>

                    <div className="rounded-lg shadow-[0px_4px_8px_-2px_rgba(16,_24,_40,_0.10),_0px_2px_4px_-2px_rgba(16,_24,_40,_0.06)] overflow-hidden">{MapComponent}</div>
                    <div className="flex justify-between items-center gap-x-2">
                        <ButtonCmp
                            type="button"
                            className="btn-outline-primary"
                            icon={<ArrowUpRight className="w-4 h-4" />}
                            onClick={() => {
                                setIsAddressOpen(true);
                            }}
                            disabled={false}
                        >
                            Open
                        </ButtonCmp>

                        <div className="flex items-center gap-x-2">
                            <ButtonCmp className="btn-link-secondary" size="none" onClick={handleEditShipper(false)}>
                                {t('Common.Cancel')}
                            </ButtonCmp>
                            <ButtonCmp
                                className="btn_primary"
                                iconSide="right"
                                onClick={handleSubmit(handleSave)}
                                loading={isLoading}
                                disabled={isLoading || (!id ? false : !hasFormChanged)}
                                icon={
                                    <>
                                        <Command className="w-4 h-4" />
                                        <span>+</span>
                                        <CornerDownLeft className="w-4 h-4" />
                                    </>
                                }
                            >
                                {t('Common.Save')}
                            </ButtonCmp>
                        </div>
                    </div>
                </div>
            </div>
            <Address
                isOpen={isAddressOpen}
                mode={watchShipperAddress ? 'shipperAddress' : 'addressCreate'}
                handleClose={() => {
                    setIsAddressOpen(false);
                }}
                setAddressData={(addressData: any) => {
                    setValue('address', addressData);
                    clearErrors('address');
                }}
                addressData={watchShipperAddress}
                status={googleMapStatus}
                alertMessageShow={isOpenFromQuoting ? false : true}
            />
        </>
    );
};
export default ShipperConsigneeForm;
